import { Component } from '@theme/component';
import { prefersReducedMotion, lockScroll, unlockScroll } from '@theme/utilities';

/**
 * CASE.SHE product media gallery.
 *
 * Uses the existing media track as a two-column grid on desktop PDPs,
 * or as a carousel with thumbnails, arrows and a compact counter
 * and a single-level fullscreen viewer for image media.
 *
 * The track is a real scroll container, so swiping and snapping keep working
 * without this module; the module adds thumbnails, arrows, the counter, the
 * viewer and the screen-reader announcements.
 *
 * All listeners hang off one AbortController that is recreated on every connect,
 * so Section Rendering, morph and a Theme Editor drag-reorder (a plain DOM move)
 * can never leave a duplicated or dead listener behind.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [track] - The main media scroll track.
 * @property {HTMLElement[]} [slides] - The main media slides.
 * @property {HTMLElement} [rail] - The thumbnail rail scroll container.
 * @property {HTMLButtonElement[]} [thumbs] - The thumbnail buttons.
 * @property {HTMLButtonElement} [previous] - Previous media button.
 * @property {HTMLButtonElement} [next] - Next media button.
 * @property {HTMLElement} [counter] - Current media number.
 * @property {HTMLElement} [liveRegion] - Gallery status region.
 * @property {HTMLDialogElement} [viewer] - The fullscreen viewer dialog.
 * @property {HTMLElement} [viewerTrack] - The viewer scroll track.
 * @property {HTMLElement[]} [viewerSlides] - The viewer slides.
 * @property {HTMLImageElement[]} [viewerImages] - The viewer images.
 * @property {HTMLButtonElement} [viewerClose] - The viewer close button.
 * @property {HTMLButtonElement} [viewerPrevious] - The viewer previous button.
 * @property {HTMLButtonElement} [viewerNext] - The viewer next button.
 * @property {HTMLElement} [viewerCounter] - Current viewer media number.
 * @property {HTMLElement} [viewerLiveRegion] - Viewer status region.
 *
 * @extends Component<Refs>
 */
export class CaseSheGallery extends Component {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {ResizeObserver | undefined} */
  #stageObserver;

  #index = 0;
  #viewerIndex = 0;
  #trackFrame = 0;
  #viewerFrame = 0;
  #gridLayout = false;

  /** @type {HTMLElement | null} */
  #returnFocus = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('pointerover', this.#handlePointerOver, { signal });

    this.refs.track?.addEventListener('scroll', this.#handleTrackScroll, { passive: true, signal });
    this.refs.viewerTrack?.addEventListener('scroll', this.#handleViewerScroll, { passive: true, signal });
    this.refs.viewer?.addEventListener('close', this.#handleViewerClose, { signal });

    this.#observeStage();

    this.#index = this.#readIndex(this.refs.track, this.#slides);
    this.#gridLayout = this.#isGrid;
    this.#renderGallery();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Reset every piece of instance state so re-attaching the same element
    // (a Theme Editor drag-reorder is a plain DOM move) starts clean.
    this.#controller?.abort();
    this.#controller = undefined;

    this.#stageObserver?.disconnect();
    this.#stageObserver = undefined;
    this.style.removeProperty('--cs-pg-stage-h');

    cancelAnimationFrame(this.#trackFrame);
    cancelAnimationFrame(this.#viewerFrame);
    this.#trackFrame = 0;
    this.#viewerFrame = 0;

    if (this.refs.viewer?.open) this.refs.viewer.close();
    unlockScroll(this);

    this.#returnFocus = null;
  }

  /** Re-reads state from the DOM after the Section Rendering API morphs this subtree. */
  updatedCallback() {
    super.updatedCallback();

    this.#index = this.#isGrid ? 0 : this.#readIndex(this.refs.track, this.#slides);
    this.#renderGallery();
  }

  /**
   * Publishes the stage's real height so the thumbnail rail can match it.
   *
   * The stage takes the shape of the product's media, so its height depends on
   * the column width and cannot be written as a CSS length. The rail's width is
   * fixed and independent of its height, so nothing here can feed back into the
   * stage and loop.
   */
  #observeStage() {
    const { track } = this.refs;
    const stage = track?.parentElement;
    if (!stage || typeof ResizeObserver === 'undefined') return;

    this.#stageObserver = new ResizeObserver(([entry]) => {
      const height = entry?.contentRect.height;
      if (!height) return;

      // Published at full precision: the image width is derived from this, so
      // rounding it to whole pixels would leave the photo a pixel short of the
      // box on wide ratios.
      this.style.setProperty('--cs-pg-stage-h', `${height}px`);

      const gridLayout = this.#isGrid;
      if (gridLayout !== this.#gridLayout) {
        this.#gridLayout = gridLayout;
        this.#scrollToIndex(this.refs.track, this.#slides, this.#index, 'instant');
        this.#renderGallery();
      }
    });

    this.#stageObserver.observe(stage);
  }

  /* ------------------------------------------------------------------ helpers */

  /** @returns {HTMLElement[]} */
  get #slides() {
    return toArray(this.refs.slides);
  }

  /** @returns {HTMLElement[]} */
  get #viewerSlideElements() {
    return toArray(this.refs.viewerSlides);
  }

  /** @returns {ScrollBehavior} */
  get #behavior() {
    return prefersReducedMotion() ? 'instant' : 'smooth';
  }

  get #isGrid() {
    return !!this.refs.track && getComputedStyle(this.refs.track).display === 'grid';
  }

  /**
   * Reads the index of the slide closest to the centre of its scroll track.
   *
   * Measured from live rectangles rather than `scrollLeft / slideWidth`, so
   * fractional container widths and sub-pixel scroll positions cannot drift.
   *
   * @param {HTMLElement | undefined} track - The scroll container.
   * @param {HTMLElement[]} slides - The slides inside it.
   * @returns {number} The active index, or 0 when there is nothing to measure.
   */
  #readIndex(track, slides) {
    if (!track || slides.length === 0) return 0;
    if (track === this.refs.track && this.#isGrid) return Math.min(this.#index, slides.length - 1);

    const trackRect = track.getBoundingClientRect();
    if (trackRect.width === 0) return 0;

    const center = trackRect.left + trackRect.width / 2;
    let closest = 0;
    let smallest = Infinity;

    slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - center);

      if (distance < smallest - 0.5) {
        smallest = distance;
        closest = index;
      }
    });

    return closest;
  }

  /**
   * Scrolls a track so the given slide is centred.
   *
   * @param {HTMLElement | undefined} track - The scroll container.
   * @param {HTMLElement[]} slides - The slides inside it.
   * @param {number} index - The slide to show.
   * @param {ScrollBehavior} behavior - Scroll behaviour.
   */
  #scrollToIndex(track, slides, index, behavior) {
    const slide = slides[index];
    if (!track || !slide) return;
    if (track === this.refs.track && this.#isGrid) return;

    const trackRect = track.getBoundingClientRect();
    const rect = slide.getBoundingClientRect();
    const delta = rect.left + rect.width / 2 - (trackRect.left + trackRect.width / 2);

    if (Math.abs(delta) < 1) return;

    track.scrollBy({ left: delta, behavior });
  }

  /**
   * Fills `%index%` / `%count%` in a localized status template.
   *
   * @param {string | undefined} template - The template string.
   * @param {number} index - One-based position.
   * @returns {string} The localized status text.
   */
  #status(template, index) {
    if (!template) return '';

    return template.replace('%index%', String(index));
  }

  /* ------------------------------------------------------------------- events */

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const control = target.closest('[data-cs-action]');
    const action = control instanceof HTMLElement ? control.dataset.csAction : undefined;

    if (action) {
      if (control instanceof HTMLElement && control.getAttribute('aria-disabled') === 'true') return;

      switch (action) {
        case 'select':
          this.select(Number(control instanceof HTMLElement ? control.dataset.csIndex : 0));
          return;
        case 'previous':
          this.select(this.#index - 1);
          return;
        case 'next':
          this.select(this.#index + 1);
          return;
        case 'open':
          this.#openViewer(Number(control instanceof HTMLElement ? control.dataset.csIndex : 0), control);
          return;
        case 'viewer-close':
          this.refs.viewer?.close();
          return;
        case 'viewer-previous':
          this.#selectViewer(this.#viewerIndex - 1);
          return;
        case 'viewer-next':
          this.#selectViewer(this.#viewerIndex + 1);
          return;
        default:
          return;
      }
    }

    // Inside the viewer, a press anywhere that is not a control closes it. The
    // image has no press behaviour of its own: there is no second zoom level, no
    // scaling and no panning, so a press on it can only ever close the viewer.
    const { viewer } = this.refs;
    if (viewer?.open && viewer.contains(target)) viewer.close();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;

    const { viewer, rail } = this.refs;

    // Escape is handled natively by <dialog>; only paging needs wiring up.
    if (viewer?.open && viewer.contains(target)) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.#selectViewer(this.#viewerIndex + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.#selectViewer(this.#viewerIndex - 1);
      }

      return;
    }

    if (!rail?.contains(target)) return;

    const thumbs = toArray(this.refs.thumbs);
    // Step from the thumbnail that actually has focus, which is not necessarily
    // the active medium.
    const focused = thumbs.findIndex((thumb) => thumb === document.activeElement);
    const current = focused === -1 ? this.#index : focused;
    let next = null;

    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowLeft':
        next = current - 1;
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        next = current + 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = thumbs.length - 1;
        break;
      default:
        return;
    }

    if (next === null || next < 0 || next > thumbs.length - 1) return;

    event.preventDefault();
    this.select(next);
    thumbs[next]?.focus({ preventScroll: true });
  };

  /** @param {PointerEvent} event */
  #handlePointerOver = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const slide = target.closest('[data-cs-viewer-index]');
    if (!(slide instanceof HTMLElement)) return;

    this.#primeViewerImages(Number(slide.dataset.csViewerIndex));
  };

  #handleTrackScroll = () => {
    cancelAnimationFrame(this.#trackFrame);

    this.#trackFrame = requestAnimationFrame(() => {
      const index = this.#readIndex(this.refs.track, this.#slides);
      if (index === this.#index) return;

      this.#index = index;
      this.#renderGallery();
    });
  };

  #handleViewerScroll = () => {
    cancelAnimationFrame(this.#viewerFrame);

    this.#viewerFrame = requestAnimationFrame(() => {
      const index = this.#readIndex(this.refs.viewerTrack, this.#viewerSlideElements);
      if (index === this.#viewerIndex) return;

      this.#viewerIndex = index;
      this.#primeViewerImages(index);
      this.#renderViewer();
    });
  };

  #handleViewerClose = () => {
    unlockScroll(this);

    // Leave the gallery on whatever the shopper looked at last.
    const galleryIndex = this.#viewerSlideElements[this.#viewerIndex]?.dataset.csIndex;
    if (galleryIndex != null) this.select(Number(galleryIndex), false);

    const target = this.#returnFocus;
    this.#returnFocus = null;

    if (target?.isConnected) target.focus({ preventScroll: true });
  };

  /* -------------------------------------------------------------- main media */

  /**
   * Shows a medium.
   *
   * @param {number} index - The media index.
   * @param {boolean} [animate] - Whether to animate the scroll.
   */
  select(index, animate = true) {
    const slides = this.#slides;
    if (!Number.isInteger(index) || index < 0 || index > slides.length - 1) return;

    const step = Math.abs(index - this.#index);
    this.#index = index;
    this.#scrollToIndex(this.refs.track, slides, index, animate && step === 1 ? this.#behavior : 'instant');
    this.#renderGallery();
  }

  #renderGallery() {
    const slides = this.#slides;
    const count = slides.length;
    if (count === 0) return;

    const index = Math.min(this.#index, count - 1);

    toArray(this.refs.thumbs).forEach((thumb, thumbIndex) => {
      thumb.setAttribute('aria-current', thumbIndex === index ? 'true' : 'false');
    });

    if (this.refs.counter) this.refs.counter.textContent = String(index + 1);

    this.refs.previous?.setAttribute('aria-disabled', index === 0 ? 'true' : 'false');
    this.refs.next?.setAttribute('aria-disabled', index === count - 1 ? 'true' : 'false');

    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = this.#status(this.dataset.statusTemplate, index + 1);
    }

    this.#revealActiveThumb(index);
  }

  /**
   * Keeps the active thumbnail visible inside the rail without scrolling the page.
   *
   * The rail's scrollbar is hidden (see `case-she-product-gallery.css`), so this
   * is the only thing that tells a shopper the rail moved with them. It scrolls
   * to the nearest position that brings the thumbnail fully into view rather
   * than centring it: the surrounding thumbnails stay where the eye left them,
   * and stepping one medium moves the rail by one thumbnail.
   *
   * Only `rail` is scrolled - never `scrollIntoView`, which walks every
   * scrollable ancestor and would move the page under the shopper.
   *
   * @param {number} index - The active media index.
   */
  #revealActiveThumb(index) {
    const { rail } = this.refs;
    const thumb = toArray(this.refs.thumbs)[index];
    if (!rail || !thumb || rail.scrollHeight <= rail.clientHeight) return;

    const railRect = rail.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();

    // The rail is padded so the focus ring (`outline-offset: 2px`) is not
    // clipped at either end. Read the gutter rather than repeating the CSS
    // value, so the two cannot drift apart.
    const styles = getComputedStyle(rail);
    const viewTop = railRect.top + (parseFloat(styles.paddingTop) || 0);
    const viewBottom = railRect.bottom - (parseFloat(styles.paddingBottom) || 0);

    let delta = 0;
    if (thumbRect.top < viewTop) {
      delta = thumbRect.top - viewTop;
    } else if (thumbRect.bottom > viewBottom) {
      delta = thumbRect.bottom - viewBottom;
    } else {
      return;
    }

    rail.scrollTo({ top: rail.scrollTop + delta, behavior: this.#behavior });
  }

  /* ---------------------------------------------------------------- viewer */

  /**
   * Opens the fullscreen viewer on the medium at a gallery index.
   *
   * @param {number} galleryIndex - The gallery media index.
   * @param {Element | null} trigger - The control that opened the viewer.
   */
  #openViewer(galleryIndex, trigger) {
    const { viewer } = this.refs;
    const slide = this.#slides[galleryIndex];
    const viewerIndex = Number(slide?.dataset.csViewerIndex ?? NaN);

    if (!viewer || !Number.isInteger(viewerIndex)) return;

    this.#returnFocus = trigger instanceof HTMLElement ? trigger : null;
    this.#viewerIndex = viewerIndex;
    this.#primeViewerImages(viewerIndex);

    viewer.showModal();
    lockScroll(this);

    // The dialog is laid out by now, so the jump can be measured and instant.
    this.#scrollToIndex(this.refs.viewerTrack, this.#viewerSlideElements, viewerIndex, 'instant');
    this.#renderViewer();

    this.refs.viewerClose?.focus({ preventScroll: true });
  }

  /**
   * Shows a medium inside the viewer.
   *
   * @param {number} index - The viewer media index.
   */
  #selectViewer(index) {
    const slides = this.#viewerSlideElements;
    if (!Number.isInteger(index) || index < 0 || index > slides.length - 1) return;

    const step = Math.abs(index - this.#viewerIndex);
    this.#viewerIndex = index;
    this.#primeViewerImages(index);
    this.#scrollToIndex(this.refs.viewerTrack, slides, index, step === 1 ? this.#behavior : 'instant');
    this.#renderViewer();
  }

  #renderViewer() {
    const count = this.#viewerSlideElements.length;
    if (count === 0) return;

    const index = Math.min(this.#viewerIndex, count - 1);

    if (this.refs.viewerCounter) this.refs.viewerCounter.textContent = String(index + 1);

    this.refs.viewerPrevious?.setAttribute('aria-disabled', index === 0 ? 'true' : 'false');
    this.refs.viewerNext?.setAttribute('aria-disabled', index === count - 1 ? 'true' : 'false');

    if (this.refs.viewerLiveRegion) {
      this.refs.viewerLiveRegion.textContent = this.#status(this.dataset.viewerStatusTemplate, index + 1);
    }
  }

  /**
   * Lets the viewer image at `index` and its neighbours load.
   *
   * Viewer images sit in a closed dialog, so `loading="lazy"` keeps them off the
   * initial page weight entirely until they are actually needed.
   *
   * @param {number} index - The viewer media index.
   */
  #primeViewerImages(index) {
    if (!Number.isInteger(index)) return;

    const images = toArray(this.refs.viewerImages);

    for (const offset of [0, 1, -1]) {
      const image = images[index + offset];
      if (image instanceof HTMLImageElement && image.loading === 'lazy') image.loading = 'eager';
    }
  }
}

/**
 * Normalizes a ref that may be missing, a single element or an array.
 *
 * @template {Element} T
 * @param {T | T[] | undefined} value - The ref value.
 * @returns {T[]} An array of elements.
 */
function toArray(value) {
  if (!value) return [];

  return Array.isArray(value) ? value : [value];
}

if (!customElements.get('case-she-gallery')) {
  customElements.define('case-she-gallery', CaseSheGallery);
}
